const V2_PRESENTATION_JSON_BASE_URL = 'https://viewer-video-presentations.flatworldknowledge.com';

export const LS_AUDIO_DESCRIBED_KEY = 'fwk-viewer-prefer-audio-described';
const PREFER_AD_CHANGE_EVENT = 'fwk-viewer-prefer-audio-described:change';

export function parseAdQuery(search) {
  if (!search) return null;
  const searchParams = new URLSearchParams(search);
  const audioDescribedParam =
    searchParams.get('ad') ?? searchParams.get('audioDescribed');
  if (audioDescribedParam === '1' || audioDescribedParam === 'true') return true;
  if (audioDescribedParam === '0' || audioDescribedParam === 'false') return false;
  return null;
}

export function readGlobalPreferAd() {
  try {
    return window.localStorage.getItem(LS_AUDIO_DESCRIBED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function readInitialPreferAd(search) {
  const fromQuery = parseAdQuery(search);
  if (fromQuery !== null) return fromQuery;
  return readGlobalPreferAd();
}

export function persistPreferAd(enabled) {
  const next = !!enabled;
  try {
    if (next) window.localStorage.setItem(LS_AUDIO_DESCRIBED_KEY, 'true');
    else window.localStorage.removeItem(LS_AUDIO_DESCRIBED_KEY);
  } catch (_) {}
  window.dispatchEvent(
    new CustomEvent(PREFER_AD_CHANGE_EVENT, { detail: next }),
  );
}

export function subscribeToGlobalPreferAd(handler) {
  const onCustom = (event) => handler(!!event.detail);
  const onStorage = (event) => {
    if (event.key !== LS_AUDIO_DESCRIBED_KEY) return;
    handler(event.newValue === 'true');
  };
  window.addEventListener(PREFER_AD_CHANGE_EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(PREFER_AD_CHANGE_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}

export async function probeAudioDescribedMetadataAvailable(internalId) {
  const audioDescribedMetadataUrl = `${V2_PRESENTATION_JSON_BASE_URL}/${internalId}/${internalId}_1_ad.json`;
  try {
    let response = await fetch(audioDescribedMetadataUrl, { method: 'HEAD' });
    if (response.ok) return true;
    if (response.status === 405 || response.status === 501) {
      response = await fetch(audioDescribedMetadataUrl);
      return response.ok;
    }
    return false;
  } catch {
    return false;
  }
}

export async function fetchPresentationMetadata(internalId, preferAd) {
  const presentationBasePath = `${V2_PRESENTATION_JSON_BASE_URL}/${internalId}`;
  const standardMetadataUrl = `${presentationBasePath}/${internalId}_1.json`;
  const responseToPresentationData = async (response) => {
    if (!response.ok) throw new Error(String(response.status));
    return { data: await response.json() };
  };
  if (!preferAd) return responseToPresentationData(await fetch(standardMetadataUrl));
  try {
    const audioDescribedResponse = await fetch(
      `${presentationBasePath}/${internalId}_1_ad.json`,
    );
    if (audioDescribedResponse.ok) {
      return { data: await audioDescribedResponse.json() };
    }
  } catch (_) {}
  return responseToPresentationData(await fetch(standardMetadataUrl));
}

export function mapPresentationJsonToPlayerState(data) {
  const { content: presentationContent } = data;
  if (presentationContent.totalParts) {
    return {
      kind: 'multipart',
      multiPartDuration: +presentationContent.videoSeconds || 0,
    };
  }
  const layoutRegions = presentationContent.region;
  const videoRegion = layoutRegions.find((layer) => layer.name === 'vid');
  const synchRegion = layoutRegions.find((layer) => layer.name === 'synch');
  return {
    kind: 'single',
    videoUrl: videoRegion.src,
    videoAspectRatio: +(+videoRegion.width / +videoRegion.height).toFixed(3),
    slides: synchRegion.img,
  };
}
